'use client';

import { CSSProperties } from 'react';
import Link from 'next/link';
import { IAlerts } from '@/.generated';
import { emergencyAlertVariants } from './EmergencyAlert.styles';
import { getCSLPAttributes } from '@/utils/type-guards';
import { trackAdobeClick } from '@/utils/adobe/click';
import { Container } from '@/components/primitives/Container/Container';
import RichTextWrapper from '../Wrappers/RichTextWrapper/RichTextWrapper';

interface EmergencyAlertItemProps {
  alert: IAlerts;
}

export const EmergencyAlertItem = ({ alert }: EmergencyAlertItemProps) => {
  const {
    alertWrapper,
    contentWrapper,
    iconWrapper,
    icon,
    textContent,
    headline,
    description,
    alertLink,
  } = emergencyAlertVariants();

  // if (isDismissed) return null;

  const alertDetails = alert.alert_details;
  const isEmergency = alert.alert_type === 'emergency-alert';
  const bgColor = alertDetails?.bg_color?.color_list;
  const textColor = alertDetails?.text_color?.color_list;
  const hasLink = alertDetails?.alert_link?.href;

  // Fire the Adobe Click event for the alert CTA
  const handleAlertClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    trackAdobeClick(
      {
        clickName: alertDetails?.alert_link?.title || 'Learn more',
        clickLocation: isEmergency ? 'Emergency-Alert' : 'Site-Alert',
        clickType: 'alert-link',
      },
      e
    );
  };

  // Apply CMS bg/text colors with fallbacks; inline style overrides Tailwind defaults
  const inlineStyle: CSSProperties = {
    backgroundColor: bgColor || '#9B2D30',
    color: textColor || '#ffffff',
  };

  return (
    // Full-width outer div carries the CMS background/text color across the entire viewport
    <div
      className="w-full relative z-30 transform-gpu"
      style={inlineStyle}
      role={isEmergency ? 'alert' : 'region'}
      aria-label={isEmergency ? 'Emergency Alert' : 'Site Alert'}
    >
      {/* Container constrains the content to 1140px (max-w-page-template) */}
      <Container
        containerBleed
        tag="div"
        horizontalPadding={false}
        bottomMargin={false}
        className="bg-transparent"
      >
        <div className={alertWrapper()}>
          <div className={contentWrapper()}>
            <div style={{ color: textColor || '#ffffff' }} className={iconWrapper()} aria-hidden="true">
              <i
                className={`${alertDetails?.alert_fav_icon || 'fa-solid fa-triangle-exclamation'} ${icon()}`}
              />
            </div>

            <div style={{ color: textColor || '#ffffff' }} className={textContent()}>
              {alertDetails?.headline && (
                <span style={{ color: textColor || '#ffffff' }} className={headline()} {...getCSLPAttributes(alertDetails.$?.headline)}>
                  {alertDetails.headline}
                </span>
              )}

              {alertDetails?.description && (
                <RichTextWrapper
                  style={{ color: textColor || '#ffffff' }}
                  content={alertDetails.description}
                  className={description()}
                  {...getCSLPAttributes(alertDetails.$?.description)}
                ></RichTextWrapper>
              )}

              {hasLink && (
                <Link
                  href={alertDetails.alert_link!.href}
                  onClick={handleAlertClick}
                  className={alertLink()}
                  {...getCSLPAttributes(alertDetails.$?.alert_link)}
                >
                  {alertDetails.alert_link!.title || 'Learn more'}
                </Link>
              )}
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};
